import React, { useState, useRef } from 'react';
import { postService, photoService } from '../../services/api';
import { FiImage, FiMapPin, FiSmile, FiX } from 'react-icons/fi';
import './CreatePost.css';

const FEELINGS = [
  { emoji: '😊', label: 'Happy' },
  { emoji: '🎉', label: 'Excited' },
  { emoji: '🙏', label: 'Grateful' },
  { emoji: '❤️', label: 'Loved' },
  { emoji: '😎', label: 'Cool' },
  { emoji: '🤔', label: 'Thoughtful' },
  { emoji: '😢', label: 'Sad' },
  { emoji: '😴', label: 'Tired' },
  { emoji: '🥳', label: 'Celebrating' },
  { emoji: '😇', label: 'Blessed' },
  { emoji: '🏠', label: 'At Home' },
  { emoji: '✈️', label: 'Traveling' },
];

const CreatePost = ({ user, onPostCreated, onClose }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Photo state
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const fileInputRef = useRef(null);
  
  // Feeling state
  const [feeling, setFeeling] = useState(null);
  const [showFeelingPicker, setShowFeelingPicker] = useState(false);
  
  // Location state
  const [location, setLocation] = useState('');
  const [showLocationInput, setShowLocationInput] = useState(false);

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Limit to 4 photos
    const newFiles = files.slice(0, 4 - selectedPhotos.length);
    
    // Validate file types and sizes
    const validFiles = newFiles.filter(file => {
      if (!file.type.startsWith('image/')) {
        setError('Please select only image files');
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('Each image must be less than 10MB');
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      setSelectedPhotos(prev => [...prev, ...validFiles]);
      
      // Create previews
      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPhotoPreviews(prev => [...prev, reader.result]);
        };
        reader.readAsDataURL(file);
      });
    }
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removePhoto = (index) => {
    setSelectedPhotos(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleFeelingSelect = (selectedFeeling) => {
    setFeeling(selectedFeeling);
    setShowFeelingPicker(false);
  };

  const clearFeeling = () => {
    setFeeling(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!content.trim() && selectedPhotos.length === 0) {
      setError('Please write something or add a photo');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Build content with feeling and location
      let fullContent = content.trim();
      
      // Upload photos first if any
      let uploadedPhotoIds = [];
      if (selectedPhotos.length > 0) {
        for (const photo of selectedPhotos) {
          const formData = new FormData();
          formData.append('file', photo);
          const photoResponse = await photoService.uploadPhoto(formData);
          if (photoResponse.success) {
            uploadedPhotoIds.push(parseInt(photoResponse.data.photoID, 10));
          }
        }
      }

      const response = await postService.createPost({
        content: fullContent,
        postType: selectedPhotos.length > 0 ? 'photo' : 'text',
        visibility: 'family',
        feeling: feeling ? `${feeling.emoji} ${feeling.label}` : null,
        locationName: location.trim() || null,
        photoIds: uploadedPhotoIds.length > 0 ? uploadedPhotoIds : null
      });

      if (response.success) {
        setContent('');
        setSelectedPhotos([]);
        setPhotoPreviews([]);
        setFeeling(null);
        setLocation('');
        setShowLocationInput(false);
        setIsExpanded(false);
        
        if (onPostCreated) {
          onPostCreated(response.data);
        }
        if (onClose) onClose();
      } else {
        setError(response.message || 'Failed to create post');
      }
    } catch (err) {
      console.error('Failed to create post:', err);
      setError('Failed to create post. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setContent('');
    setSelectedPhotos([]);
    setPhotoPreviews([]);
    setFeeling(null);
    setLocation('');
    setShowLocationInput(false);
    setShowFeelingPicker(false);
    setIsExpanded(false);
    setError('');
    if (onClose) onClose();
  };

  const handlePhotoButtonClick = () => {
    setIsExpanded(true);
    setTimeout(() => fileInputRef.current?.click(), 100);
  };

  const handleFeelingButtonClick = () => {
    setIsExpanded(true);
    setTimeout(() => setShowFeelingPicker(true), 100);
  };

  return (
    <div className="create-post card">
      <div className="create-post-header">
        {user?.profilePhotoURL ? (
          <img src={user.profilePhotoURL} alt={user.firstName} className="avatar" />
        ) : (
          <div className="avatar">
            {getInitials(user?.firstName, user?.lastName)}
          </div>
        )}
        
        {!isExpanded ? (
          <button 
            className="create-post-trigger"
            onClick={() => setIsExpanded(true)}
          >
            What's on your mind, {user?.firstName}?
          </button>
        ) : (
          <div className="create-post-expanded">
            <form onSubmit={handleSubmit}>
              {/* Feeling/Location badges */}
              {(feeling || location) && (
                <div className="create-post-badges">
                  {feeling && (
                    <span className="post-badge feeling-badge">
                      {feeling.emoji} Feeling {feeling.label}
                      <button type="button" onClick={clearFeeling}><FiX /></button>
                    </span>
                  )}
                  {location && (
                    <span className="post-badge location-badge">
                      <FiMapPin /> {location}
                      <button type="button" onClick={() => { setLocation(''); setShowLocationInput(false); }}><FiX /></button>
                    </span>
                  )}
                </div>
              )}

              <textarea
                className="create-post-input"
                placeholder={`What's on your mind, ${user?.firstName}?`}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                autoFocus
                rows={3}
              />

              {/* Photo previews */}
              {photoPreviews.length > 0 && (
                <div className={`photo-previews photo-previews-${Math.min(photoPreviews.length, 4)}`}>
                  {photoPreviews.map((preview, index) => (
                    <div key={index} className="photo-preview-item">
                      <img src={preview} alt={`Preview ${index + 1}`} />
                      <button 
                        type="button" 
                        className="photo-preview-remove"
                        onClick={() => removePhoto(index)}
                      >
                        <FiX />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Location input */}
              {showLocationInput && (
                <div className="location-input-wrapper">
                  <FiMapPin className="location-input-icon" />
                  <input
                    type="text"
                    className="location-input"
                    placeholder="Where are you?"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    autoFocus
                  />
                </div>
              )}

              {/* Feeling picker */}
              {showFeelingPicker && (
                <div className="feeling-picker">
                  <div className="feeling-picker-header">
                    <span>How are you feeling?</span>
                    <button type="button" onClick={() => setShowFeelingPicker(false)}><FiX /></button>
                  </div>
                  <div className="feeling-grid">
                    {FEELINGS.map((f) => (
                      <button
                        key={f.label}
                        type="button"
                        className={`feeling-option ${feeling?.label === f.label ? 'selected' : ''}`}
                        onClick={() => handleFeelingSelect(f)}
                      >
                        <span className="feeling-emoji">{f.emoji}</span>
                        <span className="feeling-label">{f.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {error && <div className="create-post-error">{error}</div>}

              <div className="create-post-options">
                <div className="create-post-attachments">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoSelect}
                    hidden
                  />
                  <button 
                    type="button" 
                    className={`attachment-btn ${selectedPhotos.length > 0 ? 'active' : ''}`}
                    title="Add Photo"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={selectedPhotos.length >= 4}
                  >
                    <FiImage />
                    {selectedPhotos.length > 0 && <span className="attachment-count">{selectedPhotos.length}</span>}
                  </button>
                  <button 
                    type="button" 
                    className={`attachment-btn ${showLocationInput || location ? 'active' : ''}`}
                    title="Add Location"
                    onClick={() => setShowLocationInput(!showLocationInput)}
                  >
                    <FiMapPin />
                  </button>
                  <button 
                    type="button" 
                    className={`attachment-btn ${feeling ? 'active' : ''}`}
                    title="Add Feeling"
                    onClick={() => setShowFeelingPicker(!showFeelingPicker)}
                  >
                    <FiSmile />
                  </button>
                </div>
                
                <div className="create-post-actions">
                  <button 
                    type="button" 
                    className="btn btn-ghost"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading || (!content.trim() && selectedPhotos.length === 0)}
                  >
                    {loading ? (
                      <>
                        <span className="spinner spinner-sm"></span>
                        Posting...
                      </>
                    ) : (
                      'Post'
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
      </div>

      {!isExpanded && (
        <div className="create-post-quick-actions">
          <button className="quick-action-btn" onClick={handlePhotoButtonClick}>
            <FiImage />
            <span>Photo</span>
          </button>
          <button className="quick-action-btn" onClick={handleFeelingButtonClick}>
            <FiSmile />
            <span>Feeling</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default CreatePost;
